import type { FC } from "react";
import { useEffect } from "react";

import { OUTCOME_ID_QUERY_PARAM, TAB_QUERY_PARAM } from "components/constants";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { SidebarOptionsMenu } from "components/menus/SidebarOptionsMenu";
import { AddOutcome } from "components/outcomes/AddOutcome";
import { displayOutcomeName, outcomesToOptions } from "components/outcomes/helpers";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ListItem } from "mds/components/ListItem";
import { showConfirmationModal } from "mds/components/Modal";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  NestedSectionOutcomeType,
  selectAssessedPageOutcomesByCourseOutcome,
  selectFullCourseOutcomes,
  selectOutcomes,
  selectPagesForOutcome,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { CourseContentTabKeys, withQueryParams } from "utils/urls";

interface DeleteOutcomeModalProps {
  onClose: () => void;
  onConfirm: (data?: boolean) => void;
  courseOutcome: NestedSectionOutcomeType;
  pageUsageCount: number;
}
const DeleteOutcomeModal: FC<DeleteOutcomeModalProps> = ({
  onClose,
  onConfirm,
  pageUsageCount,
  courseOutcome,
}) => {
  return (
    <div className="mt-2 flex flex-col items-start gap-4">
      <div className="flex flex-col gap-4">
        <div>{t("confirm_delete_modal.delete_label", { name: courseOutcome.outcome.title })}</div>
        <div className="border-l-2 border-red-tint-20 pl-4 font-semibold">
          {t("confirm_delete_modal.delete_warning_usage", { count: pageUsageCount })}
        </div>
      </div>

      <div className="flex w-full justify-end gap-2">
        <Button kind="white" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button kind="red" size="m" title={t("common.delete")} onClick={() => onConfirm(true)}>
          {t("common.delete")}
        </Button>
      </div>
    </div>
  );
};

const handleDeleteOutcome = async (
  courseOutcome: NestedSectionOutcomeType,
  pageUsageCount: number,
) => {
  let didDelete = false;

  if (pageUsageCount > 0) {
    didDelete = await showConfirmationModal({
      title: t("confirm_delete_modal.delete_item", { item: t("glossary.outcome") }),
      children: (onClose, onConfirm) => (
        <DeleteOutcomeModal
          courseOutcome={courseOutcome}
          pageUsageCount={pageUsageCount}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      ),
      hideCancelConfirmButtons: true,
    });
  }

  if (didDelete || pageUsageCount === 0) {
    storeApi.course_outcomes.destroy(courseOutcome.id);
    trackEvent("Course - Delete outcome", {
      ...BUTTON_EVENT_CATEGORY,
    });
  }
};

type CourseOutcomeSidebarOptionsMenuProps = {
  courseOutcome: NestedSectionOutcomeType;
};

const CourseOutcomeSidebarOptionsMenu: FC<CourseOutcomeSidebarOptionsMenuProps> = ({
  courseOutcome,
}) => {
  const { canAuthorOrg } = useCapabilities();

  const assessedPageOutcomes = useListSelector((s) =>
    selectAssessedPageOutcomesByCourseOutcome(s, courseOutcome.id),
  );
  const pagesForOutcome = useAppSelector((s) => selectPagesForOutcome(s, courseOutcome.id));
  const topicPages = pagesForOutcome.filter((p) => p.category === "activity_page");
  const assignmentPages = pagesForOutcome.filter((p) => p.category === "assignment");
  const resourcePages = pagesForOutcome.filter((p) => p.category === "course_resource");

  const pageUsageCount = topicPages.length + assignmentPages.length + resourcePages.length;

  if (!canAuthorOrg) {
    return null;
  }

  return (
    <SidebarOptionsMenu
      optionsMenuActions={{
        onDelete: () => handleDeleteOutcome(courseOutcome, pageUsageCount),
        isDeleteDisabled: assessedPageOutcomes.length > 0,
        deleteTooltipText:
          assessedPageOutcomes.length > 0
            ? t("tooltip.cannot_delete_assessed_course_outcome")
            : undefined,
      }}
    />
  );
};

type OutcomesNavSidebarProps = {
  selectedOutcome?: string;
  courseId: string;
};

export const OutcomesNavSidebar: FC<OutcomesNavSidebarProps> = ({ selectedOutcome, courseId }) => {
  const { canAuthorCourse, canAuthorOrg } = useCapabilities();

  const courseOutcomes = useAppSelector((s) => selectFullCourseOutcomes(s, courseId));
  const orgOutcomes = useAppSelector(selectOutcomes);
  // TODO: When course-only outcomes are implemented, we should add it to this list
  const filteredCourseOutcomes = courseOutcomes.filter((courseOutcome) => !!courseOutcome.outcome);

  const courseOutcomeIds = courseOutcomes.map((courseOutcome) => courseOutcome.outcome_id);
  const selectableOutcomes = orgOutcomes.filter(
    (orgOutcome) => !courseOutcomeIds.includes(orgOutcome.id),
  );

  const options = outcomesToOptions(selectableOutcomes);

  useEffect(() => {
    storeApi.outcome_assessments.list({ section_id: courseId });
  }, [courseId]);

  return (
    <div className="w-full border-r border-black-tint-95 bg-white px-4 py-3 md:w-auto md:basis-[240px]">
      {!canAuthorCourse && filteredCourseOutcomes.length === 0 && (
        <div className="body-s w-full py-2 text-black-tint-40">
          {t("outcomes.no_course_outcomes")}
        </div>
      )}
      {filteredCourseOutcomes.map((courseOutcome) => (
        <ListItem
          className="w-full pl-0"
          isSelected={courseOutcome.outcome.title === selectedOutcome}
          key={courseOutcome.outcome.title}
          to={withQueryParams("", {
            [TAB_QUERY_PARAM]: CourseContentTabKeys[1],
            [OUTCOME_ID_QUERY_PARAM]: courseOutcome.outcome.id,
          })}
        >
          <HoverableToolbar className="w-full" readOnly={!canAuthorCourse} disableUnlessEditMode>
            <div
              aria-label={`${t("glossary.outcome")} ${displayOutcomeName(courseOutcome.outcome)}`}
              className="body-s my-1 w-full cursor-pointer truncate px-2"
              role="listitem"
            >
              {displayOutcomeName(courseOutcome.outcome)}
            </div>

            <CourseOutcomeSidebarOptionsMenu courseOutcome={courseOutcome} />
          </HoverableToolbar>
        </ListItem>
      ))}

      {canAuthorCourse && canAuthorOrg && (
        <AddOutcome
          options={options}
          hideUnlessEditMode
          onSave={(orgOutcomeIds: string[]) => {
            orgOutcomeIds.forEach((orgOutcomeId) => {
              storeApi.course_outcomes // TODO: Bulk create
                .create({ section_id: courseId, outcome_id: orgOutcomeId });
            });
          }}
        />
      )}
    </div>
  );
};
